import { useState } from 'react';
import { EMPTY_BENEFICIARY } from '@shared/consumerPlan';
import { ApplicationSectionFrame } from '../components/application-section-frame';
import { SelectField, TextField } from '../components/form-fields';
import { NumberField } from '../components/number-field';
import { RepeatableEntries } from '../components/repeatable-entries';
import { useApplication } from '../hooks/use-application';
import type { ConsumerStepProps } from './step-props';

const RELATIONSHIP_OPTIONS = ['Spouse or partner', 'Child', 'Sibling', 'Someone else'].map(value => ({
  value,
  label: value,
}));

export function ApplicationBeneficiariesStep({ clientId }: ConsumerStepProps) {
  const { answers } = useApplication(clientId);
  const [beneficiaries, setBeneficiaries] = useState(answers.beneficiaries);
  const totalShare = beneficiaries.reduce((sum, beneficiary) => sum + (beneficiary.sharePercent ?? 0), 0);

  return (
    <ApplicationSectionFrame
      clientId={clientId}
      section="beneficiaries"
      title="Your potential beneficiaries"
      subtitle="This policy pays a benefit to the people you choose. Add who they are and each person’s share."
      draft={{ beneficiaries }}
    >
      <RepeatableEntries
        entries={beneficiaries}
        onChange={setBeneficiaries}
        createEntry={() => ({ ...EMPTY_BENEFICIARY })}
        entryTitle={(_, index) => `Beneficiary ${index + 1}`}
        addLabel="Add a beneficiary"
        keepFirst
        renderEntry={(beneficiary, update) => (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Full name" value={beneficiary.fullName} onChange={fullName => update({ fullName })} />
            <SelectField
              label="Relationship to you"
              value={beneficiary.relationship}
              onChange={relationship => update({ relationship })}
              options={RELATIONSHIP_OPTIONS}
            />
            <NumberField
              label="Share of the benefit (%)"
              value={beneficiary.sharePercent}
              onChange={sharePercent => update({ sharePercent })}
              min={0}
              max={100}
            />
            <TextField
              label="Date of birth (optional)"
              type="date"
              value={beneficiary.dateOfBirth}
              onChange={dateOfBirth => update({ dateOfBirth })}
            />
          </div>
        )}
      />
      {totalShare > 0 && totalShare !== 100 && (
        <p className="text-yellow-800 text-sm">Shares add up to {totalShare}%. Our team will confirm them with you.</p>
      )}
    </ApplicationSectionFrame>
  );
}
